import React, { useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { includes } from 'lodash';
import { Radio } from '@mui';
import { categoryToInt } from '@utils/filter-utils';
import FilterGroupItem from './filter-group-item';
import '../filter.scss';

const FilterGroup = ({
  category,
  enabled = true,
  filterEnabled,
  filterName,
  getIcon,
  items,
  negateIfEmpty,
  radio,
  setFilters,
  title,
  values
}) => {
  const getFilterNameValue = useCallback(() => {
    if (!category) {
      return filterName;
    }
    // Since we can have more than one category per entity,
    // each category must have their own filter slot.
    return `${filterName}|${category}`;
  }, [category, filterName]);

  const currentValues = useMemo(() => {
    const name = getFilterNameValue();
    // Get the current filters for the specified name:
    const current = values[name];

    if (R.isNil(current) || R.isEmpty(current)) {
      return [];
    }

    // Convert to array:
    const currentAsString = String(current);
    return currentAsString.split(',').map(id => categoryToInt(id));
  }, [getFilterNameValue, values]);

  const getNegatedFilterName = useCallback(() => {
    const name = getFilterNameValue();
    return name.replace(new RegExp('__in$'), '__not_in');
  }, [getFilterNameValue]);

  const onToggleCheckbox = useCallback(id => {
    const name = getFilterNameValue();

    let newIds = null;

    if (includes(currentValues, id)) {
      // If the id is in the list, toggle it off:
      newIds = currentValues.filter(currentId => currentId !== id);
    } else {
      // Else add it to the list:
      newIds = [...currentValues, id];
    }

    const isEmpty = R.isEmpty(newIds);
    if (isEmpty) {
      // If there are no ids, use a value to flag that this
      // filter is off and should be removed.
      newIds = ['clear'];
    }

    const newFilter = {
      [name]: newIds.join(',')  // Convert to string to apply it.
    };

    if (negateIfEmpty) {
      if (isEmpty) {
        const allValues = items.map(item => item.id).join(',');
        newFilter[getNegatedFilterName()] = allValues;
      } else {
        newFilter[getNegatedFilterName()] = '';
      }
    }

    setFilters(newFilter);
  }, [currentValues, getFilterNameValue, getNegatedFilterName, items, negateIfEmpty, setFilters]);

  const isAllSelected = useMemo(() => {
    return items.filter(item => includes(currentValues, item.id)).length === items.length;
  }, [currentValues, items]);

  const onSelectAll = useCallback(() => {
    if (!filterEnabled) {
      return;
    }
    const name = getFilterNameValue();
    const allValues = items.map(item => item.id).join(',');
    if (isAllSelected) {
      const itemIds = items.map(item => item.id);
      const newIds = currentValues.filter(currentId => !includes(itemIds, currentId));
      const newFilter = { [name]: newIds.join(',') };
      if (negateIfEmpty) {
        newFilter[getNegatedFilterName()] = allValues;
      }
      if (category && !newFilter[name]) {
        newFilter[name] = category;
      }
      setFilters(newFilter);
      return;
    }
    const newFilter = { [name]: allValues };
    if (negateIfEmpty) {
      newFilter[getNegatedFilterName()] = '';
    }
    setFilters(newFilter);
  }, [category, currentValues, filterEnabled, getFilterNameValue, getNegatedFilterName, isAllSelected, items, negateIfEmpty, setFilters]);

  const getAllLabel = useCallback(() => {
    if (isAllSelected) {
      return 'UNSELECT ALL';
    }
    return 'SELECT ALL';
  }, [isAllSelected]);

  const onRadioChange = useCallback(event => {
    radio.onCheck(event, event.target.value);
  }, [radio]);

  const renderRadio = () => (
    <div styleName="radio">
      <Radio
        disabled={!filterEnabled}
        checked={radio.checked}
        onChange={onRadioChange}
        value={radio.value}
        inputProps={{ 'aria-label': getAllLabel() }}
        color="primary"
        style={{ padding: 0, marginRight: '0.5rem' }}
      />
    </div>
  );

  // A checkbox/radio group needs a real <fieldset>/<legend> (a WAVE "Missing fieldset"
  // error, not satisfied by role="group"/aria-labelledby alone) - but <legend> is only
  // valid HTML as a fieldset's own direct child, so the wrapper element itself has to
  // switch, not just gain an ARIA role. Falls back to a plain div when there's no title
  // to use as the legend, since a fieldset without one is itself invalid.
  const Container = title ? 'fieldset' : 'div';

  return (
    <Container styleName="content-filter-group">
      {title && (
        <legend styleName="title">
          {radio && renderRadio()}
          <div styleName="label">{title}</div>
          <button
            aria-label={`${getAllLabel()} ${title || ''}`}
            disabled={!filterEnabled}
            onClick={onSelectAll}
            style={{ background: 'none', border: 0, fontFamily: 'inherit', padding: 0 }}
            styleName={`button ${filterEnabled ? '' : 'disabled'}`}
            type="button"
          >
            {getAllLabel()}
          </button>
        </legend>
      )}
      <div styleName="list">
        {items.map(item => (
          <FilterGroupItem
            currentValues={currentValues}
            enabled={enabled}
            filterEnabled={filterEnabled}
            filterName={filterName}
            getIcon={getIcon}
            key={item.id}
            item={item}
            onToggleCheckbox={onToggleCheckbox}
          />)
        )}
      </div>
    </Container>
  );
};

FilterGroup.propTypes = {
  category: PropTypes.string,
  enabled: PropTypes.bool,
  filterEnabled: PropTypes.bool,
  filterName: PropTypes.string,
  getIcon: PropTypes.func,
  items: PropTypes.array,
  negateIfEmpty: PropTypes.bool,
  radio: PropTypes.object,
  setFilters: PropTypes.func,
  title: PropTypes.string,
  values: PropTypes.object
};

export default memo(FilterGroup);
